'use client';

import { create } from 'zustand';

/**
 * Registration store — pure checkout form-value buffer for steps 3-4.
 *
 * Auth actions live in useRegistrationAuth hook.
 * Ride selection lives in RegisterPage URL params + component state.
 * Step tracking lives in RegisterPage component state.
 * No persist middleware — server-backed via entrant_history.checkout_state.
 */
interface RegistrationState {
  // Step 3: Preferences
  newsletterOptIn: boolean;
  triumphMarketingOptIn: boolean;
  movemberMarketingOptIn: boolean;
  /** Campaign-configurable opt-ins keyed by campaignSettings.marketingOptIns key */
  marketingOptIns: Record<string, boolean>;
  setNewsletterOptIn: (optIn: boolean) => void;
  setTriumphMarketingOptIn: (optIn: boolean) => void;
  setMovemberMarketingOptIn: (optIn: boolean) => void;
  setMarketingOptIn: (key: string, optIn: boolean) => void;
  /** Seed defaults for opt-in keys not already present (hydrated values win) */
  seedMarketingOptIns: (defaults: Record<string, boolean>) => void;

  // Step 4: Donation
  donationAmount: number | null;
  customDonation: string;
  setDonationAmount: (amount: number | null) => void;
  setCustomDonation: (amount: string) => void;

  // Step 4: Merchandise
  selectedMerch: Record<string, number>;
  updateMerchQuantity: (itemId: string, qty: number) => void;
  removeMerch: (itemId: string) => void;
  clearCart: () => void;

  // Step 4: Shipping
  shippingAddress: string;
  shippingAddressLine2: string;
  shippingCity: string;
  shippingState: string;
  shippingPostcode: string;
  shippingCountry: string;
  setShipping: (shipping: {
    address?: string;
    addressLine2?: string;
    city?: string;
    state?: string;
    postcode?: string;
    country?: string;
  }) => void;

  // Computed helpers
  getActualDonation: () => number;
  getMerchTotal: (merchandiseItems: Array<{ id: string; price: number }>) => number;
  getOrderTotal: (merchandiseItems: Array<{ id: string; price: number }>) => number;

  // Server-backed checkout state hydration (steps 3-4 form values only)
  hydrateFromServer: (checkoutState: Record<string, unknown>) => void;
  getCheckoutStateSnapshot: () => Record<string, unknown>;

  reset: () => void;
}

const initialState = {
  newsletterOptIn: true,
  triumphMarketingOptIn: false,
  movemberMarketingOptIn: false,
  marketingOptIns: {} as Record<string, boolean>,
  donationAmount: null as number | null,
  customDonation: '',
  selectedMerch: {} as Record<string, number>,
  shippingAddress: '',
  shippingAddressLine2: '',
  shippingCity: '',
  shippingState: '',
  shippingPostcode: '',
  shippingCountry: '',
};

export const useRegistrationStore = create<RegistrationState>()((set, get) => ({
  ...initialState,

  // Step 3: Preferences
  setNewsletterOptIn: (optIn) => set({ newsletterOptIn: optIn }),
  setTriumphMarketingOptIn: (optIn) => set({ triumphMarketingOptIn: optIn }),
  setMovemberMarketingOptIn: (optIn) => set({ movemberMarketingOptIn: optIn }),
  setMarketingOptIn: (key, optIn) =>
    set((state) => ({ marketingOptIns: { ...state.marketingOptIns, [key]: optIn } })),
  seedMarketingOptIns: (defaults) =>
    set((state) => ({ marketingOptIns: { ...defaults, ...state.marketingOptIns } })),

  // Step 4: Donation
  setDonationAmount: (amount) => set({ donationAmount: amount, customDonation: '' }),
  setCustomDonation: (amount) => set({ customDonation: amount, donationAmount: null }),

  // Step 4: Merchandise
  updateMerchQuantity: (itemId, qty) =>
    set((state) => {
      if (qty <= 0) {
        const rest = { ...state.selectedMerch };
        delete rest[itemId];
        return { selectedMerch: rest };
      }
      return { selectedMerch: { ...state.selectedMerch, [itemId]: qty } };
    }),

  removeMerch: (itemId) =>
    set((state) => {
      const rest = { ...state.selectedMerch };
      delete rest[itemId];
      return { selectedMerch: rest };
    }),

  clearCart: () => set({ donationAmount: null, customDonation: '', selectedMerch: {} }),

  // Step 4: Shipping
  setShipping: (shipping) =>
    set((state) => ({
      shippingAddress: shipping.address ?? state.shippingAddress,
      shippingAddressLine2: shipping.addressLine2 ?? state.shippingAddressLine2,
      shippingCity: shipping.city ?? state.shippingCity,
      shippingState: shipping.state ?? state.shippingState,
      shippingPostcode: shipping.postcode ?? state.shippingPostcode,
      shippingCountry: shipping.country ?? state.shippingCountry,
    })),

  // Computed helpers
  getActualDonation: () => {
    const { customDonation, donationAmount } = get();
    return customDonation ? parseFloat(customDonation) || 0 : donationAmount || 0;
  },

  getMerchTotal: (merchandiseItems) => {
    const { selectedMerch } = get();
    return Object.entries(selectedMerch).reduce((total, [itemId, qty]) => {
      const item = merchandiseItems.find((m) => m.id === itemId);
      return total + (item?.price || 0) * qty;
    }, 0);
  },

  getOrderTotal: (merchandiseItems) => {
    return get().getActualDonation() + get().getMerchTotal(merchandiseItems);
  },

  hydrateFromServer: (checkoutState: Record<string, unknown>) => {
    set({
      ...(typeof checkoutState.newsletterOptIn === 'boolean'
        ? { newsletterOptIn: checkoutState.newsletterOptIn }
        : {}),
      ...(typeof checkoutState.triumphMarketingOptIn === 'boolean'
        ? { triumphMarketingOptIn: checkoutState.triumphMarketingOptIn }
        : {}),
      ...(typeof checkoutState.movemberMarketingOptIn === 'boolean'
        ? { movemberMarketingOptIn: checkoutState.movemberMarketingOptIn }
        : {}),
      ...(checkoutState.marketingOptIns &&
      typeof checkoutState.marketingOptIns === 'object' &&
      !Array.isArray(checkoutState.marketingOptIns)
        ? {
            marketingOptIns: Object.fromEntries(
              Object.entries(checkoutState.marketingOptIns as Record<string, unknown>).filter(
                ([, v]) => typeof v === 'boolean',
              ),
            ) as Record<string, boolean>,
          }
        : {}),
      ...(typeof checkoutState.donationAmount === 'number'
        ? { donationAmount: checkoutState.donationAmount }
        : {}),
      ...(typeof checkoutState.customDonation === 'string'
        ? { customDonation: checkoutState.customDonation }
        : {}),
      ...(checkoutState.selectedMerch &&
      typeof checkoutState.selectedMerch === 'object' &&
      !Array.isArray(checkoutState.selectedMerch)
        ? {
            selectedMerch: Object.fromEntries(
              Object.entries(checkoutState.selectedMerch as Record<string, unknown>).filter(
                ([, v]) => typeof v === 'number',
              ),
            ) as Record<string, number>,
          }
        : {}),
      ...(typeof checkoutState.shippingAddress === 'string'
        ? { shippingAddress: checkoutState.shippingAddress }
        : {}),
      ...(typeof checkoutState.shippingAddressLine2 === 'string'
        ? { shippingAddressLine2: checkoutState.shippingAddressLine2 }
        : {}),
      ...(typeof checkoutState.shippingCity === 'string'
        ? { shippingCity: checkoutState.shippingCity }
        : {}),
      ...(typeof checkoutState.shippingState === 'string'
        ? { shippingState: checkoutState.shippingState }
        : {}),
      ...(typeof checkoutState.shippingPostcode === 'string'
        ? { shippingPostcode: checkoutState.shippingPostcode }
        : {}),
      ...(typeof checkoutState.shippingCountry === 'string'
        ? { shippingCountry: checkoutState.shippingCountry }
        : {}),
    });
  },

  getCheckoutStateSnapshot: () => {
    const state = get();
    return {
      newsletterOptIn: state.newsletterOptIn,
      triumphMarketingOptIn: state.triumphMarketingOptIn,
      movemberMarketingOptIn: state.movemberMarketingOptIn,
      marketingOptIns: state.marketingOptIns,
      donationAmount: state.donationAmount,
      customDonation: state.customDonation,
      selectedMerch: state.selectedMerch,
      shippingAddress: state.shippingAddress,
      shippingAddressLine2: state.shippingAddressLine2,
      shippingCity: state.shippingCity,
      shippingState: state.shippingState,
      shippingPostcode: state.shippingPostcode,
      shippingCountry: state.shippingCountry,
    };
  },

  reset: () => set({ ...initialState }),
}));
